import { ExternalLink } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { IgDialog, IgLink, IgTypography } from "@intelligems/ig-theme";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useLoggedInUser } from "@src/api/user-auths/login";
import { useDialog, useDialogs } from "@src/hooks/dialog";

const LOAD_TIMEOUT_MS = 10000;

interface RequestFeatureAccessPylonDialogProps {
  requestFormUrl: string;
  title: string;
}
export const RequestFeatureAccessPylonDialog = ({
  requestFormUrl,
  title,
}: RequestFeatureAccessPylonDialogProps) => {
  const dialog = useDialog("requestFeatureAccessPylon");
  const { closeDialog } = useDialogs();
  const { data: orgData } = useGetOrg();
  const { data: userData } = useLoggedInUser();

  const [isLoading, setIsLoading] = useState(true);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const isLoadingRef = useRef(true);

  const org = orgData?.org;
  const user = userData?.user;

  const handleClose = () => {
    closeDialog("requestFeatureAccessPylon");
    setIsLoading(true);
    setHasTimedOut(false);
  };

  const handleIframeLoad = useCallback(() => {
    isLoadingRef.current = false;
    setIsLoading(false);
    setHasTimedOut(false);
  }, []);

  useEffect(() => {
    if (!dialog.isOpen) return;

    isLoadingRef.current = true;

    const timer = setTimeout(() => {
      if (isLoadingRef.current) {
        setHasTimedOut(true);
        setIsLoading(false);
      }
    }, LOAD_TIMEOUT_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [dialog.isOpen]);

  const iframeLink = useMemo(() => {
    const result = new URL(requestFormUrl);

    if (user?.firstName || user?.lastName) {
      const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
      result.searchParams.append("name", name);
    }

    if (user?.email) {
      result.searchParams.append("email", user.email);
    }

    if (org?.shopId) {
      result.searchParams.append("shopify_url", org.shopId);
    }

    return result.toString();
  }, [org, user, requestFormUrl]);

  const renderContent = () => {
    if (hasTimedOut) {
      return (
        <Stack
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 400,
            gap: 2,
          }}
        >
          <IgTypography>
            The form could not be loaded. Please try opening it directly.
          </IgTypography>
          <IgLink
            href={iframeLink}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            Open Form
            <ExternalLink size={16} />
          </IgLink>
        </Stack>
      );
    }

    return (
      <Stack sx={{ flex: 1, minHeight: 600, position: "relative" }}>
        {isLoading && (
          <Stack
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
              backgroundColor: "background.paper",
            }}
          >
            <CircularProgress />
          </Stack>
        )}
        <iframe
          src={iframeLink}
          title={title}
          onLoad={handleIframeLoad}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          style={{
            width: "100%",
            height: "100%",
            minHeight: 600,
            border: "none",
            display: isLoading ? "none" : "block",
          }}
        />
      </Stack>
    );
  };

  return (
    <IgDialog
      showCloseIcon
      dialogHeight={null}
      handleClose={handleClose}
      showDialog={dialog.isOpen}
      title=" "
      dontUseDialogContent
      hideActions
      maxWidth="md"
      rawContent={renderContent()}
    />
  );
};
